import type {
  IRoutineRepository,
  ListRoutinesQuery,
  CreateRoutineInput,
  UpdateRoutineInput,
  PaginatedResponse,
} from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type {
  Routine,
  RoutineActionCatalogEntry,
  RoutineCronValidationResult,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

const KNOWN_CATEGORIES: Routine['category'][] = [
  'billing',
  'access',
  'gdpr',
  'restrictions',
  'maintenance',
  'custom',
];

const CATEGORY_BY_PREFIX: Record<string, Routine['category']> = {
  billing: 'billing',
  access: 'access',
  gdpr: 'gdpr',
  restrictions: 'restrictions',
  maintenance: 'maintenance',
};

function categoryFromAction(actionKey: string): Routine['category'] {
  const prefix = actionKey.split('.')[0];
  return CATEGORY_BY_PREFIX[prefix] ?? 'custom';
}

function normalizeRoutine(raw: Record<string, unknown>): Routine {
  const actionKey = (raw.actionKey ?? raw.action ?? '') as string;
  const category = (raw.category || categoryFromAction(actionKey)) as Routine['category'];
  const now = new Date().toISOString();
  return {
    id: raw.id as string,
    tenantId: raw.tenantId as string,
    code: (raw.code as string) ?? actionKey,
    name: raw.name as string,
    description: raw.description as string | undefined,
    category: KNOWN_CATEGORIES.includes(category) ? category : 'custom',
    action: (raw.action as string) ?? actionKey,
    params: (raw.params as Record<string, unknown>) ?? {},
    cronExpression: raw.cronExpression as string,
    timezone: (raw.timezone as string) ?? 'UTC',
    enabled: raw.enabled as boolean,
    isSystem: raw.isSystem as boolean,
    status: (raw.status as Routine['status']) ?? 'idle',
    triggerType: (raw.triggerType as Routine['triggerType']) ?? 'custom_cron',
    triggerSummary: (raw.triggerSummary as string) ?? '',
    lastRunAt: raw.lastRunAt as string | undefined,
    lastStatus: (raw.lastRunStatus as Routine['lastStatus']) ?? (raw.lastStatus as Routine['lastStatus']),
    lastDurationMs: (raw.lastRunDurationMs as number | undefined) ?? (raw.lastDurationMs as number | undefined),
    nextRunAt: raw.nextRunAt as string | undefined,
    failureCount: (raw.failureCount as number) ?? 0,
    createdAt: (raw.createdAt as string) ?? now,
    updatedAt: (raw.updatedAt as string) ?? now,
  };
}

function normalizePage<T extends Record<string, unknown>>(items: T[]): Routine[] {
  return items.map(normalizeRoutine);
}

function toCreatePayload(input: CreateRoutineInput): Record<string, unknown> {
  return {
    name: input.name,
    actionKey: input.action,
    cronExpression: input.cronExpression,
    ...(input.description !== undefined ? { description: input.description } : {}),
    ...(input.timezone !== undefined ? { timezone: input.timezone } : {}),
    ...(input.params !== undefined ? { params: input.params } : {}),
    ...(input.isActive !== undefined ? { enabled: input.isActive } : {}),
  };
}

function toUpdatePayload(input: UpdateRoutineInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (input.name !== undefined) payload.name = input.name;
  if (input.action !== undefined) payload.actionKey = input.action;
  if (input.cronExpression !== undefined) payload.cronExpression = input.cronExpression;
  if (input.description !== undefined) payload.description = input.description;
  if (input.timezone !== undefined) payload.timezone = input.timezone;
  if (input.params !== undefined) payload.params = input.params;
  if (input.isActive !== undefined) payload.enabled = input.isActive;
  return payload;
}

export class ApiRoutineRepository implements IRoutineRepository {
  async list(query?: ListRoutinesQuery): Promise<PaginatedResponse<Routine>> {
    const client = getApiClient();
    const qs = buildQueryString({
      page: query?.page ?? DEFAULT_PAGE,
      limit: query?.limit ?? DEFAULT_PAGE_SIZE,
      search: query?.search,
      category: query?.category,
      enabled: query?.enabled,
    });
    const response = await client.get<PaginatedResponse<Record<string, unknown>>>(`v1/scheduled-jobs?${qs}`);
    const data = normalizePage(response.data);
    return { ...response, data };
  }

  async findById(id: string): Promise<Routine | null> {
    const client = getApiClient();
    const raw = await client.get<Record<string, unknown>>(`v1/scheduled-jobs/${id}`);
    return normalizeRoutine(raw);
  }

  async create(input: CreateRoutineInput): Promise<Routine> {
    const client = getApiClient();
    const raw = await client.post<Record<string, unknown>>('v1/scheduled-jobs', toCreatePayload(input));
    return normalizeRoutine(raw);
  }

  async update(id: string, input: UpdateRoutineInput): Promise<Routine> {
    const client = getApiClient();
    const raw = await client.patch<Record<string, unknown>>(`v1/scheduled-jobs/${id}`, toUpdatePayload(input));
    return normalizeRoutine(raw);
  }

  async delete(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/scheduled-jobs/${id}`);
  }

  async toggle(id: string, enabled: boolean): Promise<Routine> {
    const client = getApiClient();
const raw = await client.post<Record<string, unknown>>(`v1/scheduled-jobs/${id}/toggle`, { enabled });
    return normalizeRoutine(raw);
  }

  async trigger(id: string, params?: Record<string, unknown>): Promise<{ executionId: string }> {
    const client = getApiClient();
    return client.post<{ executionId: string }>(`v1/scheduled-jobs/${id}/trigger`, { params });
  }

  async validateCron(cronExpression: string, timezone?: string): Promise<RoutineCronValidationResult> {
    const client = getApiClient();
    return client.post<RoutineCronValidationResult>('v1/scheduled-jobs/validate-cron', {
      cronExpression,
      ...(timezone !== undefined ? { timezone } : {}),
    });
  }

  async listActions(): Promise<RoutineActionCatalogEntry[]> {
    const client = getApiClient();
    return client.get<RoutineActionCatalogEntry[]>('v1/scheduled-jobs/actions');
  }
}
